<template>
  <v-card class="risk-card" width="100%" height="250" elevation="5" :style="riskHeaderStyle">
    <v-list-item one-line class="risk-card__title">
      <v-list-item-content>
        <v-list-item-title class="d-flex text-h5"> Risk </v-list-item-title>
      </v-list-item-content>
      <v-list-item-action v-if="$slots.action">
        <slot name="action"></slot>
      </v-list-item-action>
    </v-list-item>
    <v-card-text class="justify-center align-center text-center primary--text">
      <BigNumber
        v-if="assignedValue"
        title=""
        :value="riskValue"
        :subtitle="consequence && consequence.scale ? consequence.scale : ''"
        value-font="display-4 primary--text"
        :big-icon="riskIcon"
        :big-icon-color="riskCalculatedColor"
      ></BigNumber>
      <v-icon v-else center size="90" color="primary">fa-solid fa-empty-set</v-icon>
      <div v-if="subtitle" class="pt-4 subtitle-2 primary--text">
        <span>{{ subtitle.name }}: </span>
        <template v-if="subtitle.value !== null">${{ subtitle.value }}</template>
        <v-icon small v-else color="primary">fa-regular fa-empty-set</v-icon>
      </div>
      <template v-if="!inAnalysisContext && analysis">
        <div>
          <template v-if="!assignedFailureModeValue"><br /></template>
          <router-link class="pt-4 subtitle-2 primary--text text-no-wrap" :to="'/apps/riskanalyses/' + analysis.id">
            Open analysis '{{ analysis.name }}'
          </router-link>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>
<script>
import { BigNumber, GradientCalculator, RISK_ICON_REG } from '@itus/ui.common'

export default {
  name: 'RiskCard',
  props: {
    probability: {
      type: Object,
      description: 'Probability object'
    },
    consequence: {
      type: Object,
      description: 'Consequence object'
    },
    failureModeRisk: {
      type: Number,
      description: 'Falure Mode driving risk.'
    },
    analysis: {
      type: Object,
      description: 'The risk analysis in context (optional)',
      default: null
    },
    inAnalysisContext: Boolean,
    analysisProbabilityValue: {
      type: Number,
      description: 'Calculated analysis probability.'
    }
  },
  data() {
    return {
      riskIcon: RISK_ICON_REG,
      gradientCalculator: null,
      maxRiskThreshold: 1000000,
      gradientDisabled: 'rgb(200,200,200)',
      gradientStart: 'rgb(254,252,252)',
      gradientEnd: 'rgb(183,28,28)',
      gradientSteps: 1000
    }
  },
  components: {
    BigNumber
  },
  computed: {
    riskHeaderStyle() {
      return `border-top: 10px solid ${this.riskCalculatedColor};`
    },
    assignedValue() {
      let validProbability =
        this.probability &&
        this.probability.id !== 'notdefined' &&
        Number.isInteger(this.probability.value) &&
        this.probability.value >= 0 &&
        this.probability.value <= 100

      if (!validProbability) {
        return false
      }

      return (
        this.consequence &&
        this.consequence.id !== 'notdefined' &&
        Number.isInteger(this.consequence.value) &&
        this.consequence.value >= 0
      )
    },
    failureModeRiskText() {
      return this.assignedFailureModeValue ? this.failureModeRisk.toLocaleString() : null
    },
    assignedFailureModeValue() {
      return this.failureModeRisk !== null && this.failureModeRisk !== undefined && !isNaN(this.failureModeRisk)
    },
    riskValue() {
      if (!this.assignedValue) {
        return null
      } else {
        return (this.probability.value / 100) * this.consequence.value
      }
    },
    riskCalculatedColor() {
      if (!this.riskValue) {
        return this.gradientDisabled
      } else if (this.riskValue <= 0) {
        return this.gradientStart
      } else if (this.riskValue >= this.maxRiskThreshold) {
        return this.gradientEnd
      } else {
        return this.gradientCalculator.rgbColor(Math.round(this.riskValue / this.gradientSteps))
      }
    },
    assignedAnalysisProbabilityValue() {
      return this.analysisProbabilityValue !== null && this.analysisProbabilityValue !== undefined
    },
    analysisRiskValue() {
      if (
        !this.assignedAnalysisProbabilityValue ||
        !this.consequence ||
        this.consequence.value === null ||
        this.consequence.value === undefined ||
        (!this.assignedFailureModeValue && this.consequence.lifeCycleState === 'notdefined')
      ) {
        return null
      }
      let riskValue = Math.trunc((this.analysisProbabilityValue / 100) * this.consequence.value)
      if (isNaN(riskValue)) {
        return null
      }
      return riskValue.toLocaleString()
    },
    assignedAnalysisRiskValue() {
      return this.analysisRiskValue !== null
    },
    subtitle() {
      const analysisName = 'Analysis Risk'
      const failureModeName = 'Failure Mode Risk'
      if (this.inAnalysisContext) {
        if (!this.probability.twinId) {
          return null
        }
        if (this.assignedAnalysisRiskValue) {
          return { name: analysisName, value: this.analysisRiskValue }
        } else if (this.assignedFailureModeValue) {
          return { name: failureModeName, value: this.failureModeRiskText }
        }
        return { name: analysisName, value: null }
      } else if (!this.assignedValue && this.assignedFailureModeValue) {
        return { name: failureModeName, value: this.failureModeRiskText }
      }
      return null
    }
  },
  created() {
    this.gradientCalculator = new GradientCalculator(this.gradientStart, this.gradientEnd, this.gradientSteps)
  }
}
</script>
<style lang="scss" scoped>
.risk-card {
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;

  .risk-card__title {
    height: 56px;
  }
}
</style>
